import { Info } from '@phosphor-icons/react';
import { ExistingDDList } from '../components/ExistingDDList';
import { NewDDList } from '../components/NewDDList';
import { useEffect, useState } from 'react';
import { updateDrillDownTables } from './api/updateDrillDownTables';
import { isEqual, uniq } from 'lodash';
import { DrillDownTables } from '../components/DrillDownTables';
import { DrillDownPaths } from '../components/DrillDownPaths';
import useExploreStore from '../store/useExploreStore';
import { useShallow } from 'zustand/react/shallow';

export const SelectDrillDowns = ({
  searchMetricDetail,
  newMetricDetails,
  reportMsgFetch,
  logoList,
  selTables,
  setSelTables,
  bkpSelTables,
  setBkpSelTables,
  pathDetails,
  setPathDetails,
  tablePaths,
  setTablePaths,
  bkpTablePaths,
  setBkpTablePaths,
  loading,
  setLoading,
  selected,
  setSelected,
  pathsLoading = false,
}) => {
  // console.log(searchMetricDetail);
  // console.log(newMetricDetails);
  // console.log(reportMsgFetch.data);
  // console.log(logoList);

  const [dimensions, setDimensions] = useState({
    height: window.innerHeight,
    width: window.innerWidth,
  });

  const { drillDownPathsRefetch, setDrillDownPathsRefetch } = useExploreStore(
    useShallow((state) => ({
      drillDownPathsRefetch: state.defaultDrillDownPath.refetch,
      setDrillDownPathsRefetch: state.setDrillDownPathsRefetch,
    }))
  );

  const updateKDDrillDowns = updateDrillDownTables();

  const updateDrillDownTableHandler = async () => {
    const tabData = structuredClone(selTables || []);
    const uniqueIds = uniq(tabData.map((item) => item.integId));
    let data = [];
    if (uniqueIds.length > 0) {
      uniqueIds.forEach((id) => {
        const temp = tabData.filter((integ) => integ.integId === id);
        const tempData = {
          name: temp[0].integName,
          dataSourceId: temp[0].integId,
          platform: temp[0].platform,
          tables: temp.map((integ) => ({
            name: integ.tableName,
            dictionaryId: integ.tableId,
          })),
        };
        data.push(tempData);
      });
    }
    try {
      setLoading(true);
      const res = await updateKDDrillDowns.mutateAsync({
        metricId: newMetricDetails.metricId,
        drillDownSources: data,
      });
      setPathDetails(res);
      let paths = {};
      const tables = Object.keys(res?.mergePaths || {});
      if (Array.isArray(tables) && tables.length > 0) {
        tables.forEach((p) => {
          const allPaths = res.mergePaths[p]?.paths;
          if (Array.isArray(allPaths)) {
            if (allPaths.length > 0) {
              if (tablePaths?.[p]) {
                paths[p] = tablePaths[p];
              } else {
                paths[p] = allPaths[0];
              }
            } else if (allPaths.length === 0) {
              if (p === newMetricDetails?.primarySource) {
                paths[p] = [];
              }
            }
          }
        });
      }
      setTablePaths(paths);
      setBkpSelTables(structuredClone(selTables || []));
      setLoading(false);
    } catch (e) {
      setLoading(false);
      return Promise.reject(e);
    }
  };

  useEffect(() => {
    const handleResize = (e) => {
      setDimensions({
        height: e?.target?.innerHeight,
        width: e?.target?.innerWidth,
      });
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (drillDownPathsRefetch) {
      updateDrillDownTableHandler();
      setDrillDownPathsRefetch(false);
    }
  }, [drillDownPathsRefetch]);

  return (
    <div
      className="w-full h-full flex flex-col"
      style={{
        zIndex: 0,
      }}
    >
      <div
        className="flex px-4 py-2 items-center justify-between border-b border-pv-neutral-grey-100"
        style={{
          zIndex: 1,
        }}
      >
        <div className="flex flex-col">
          <span className="font-medium">Drill-downs</span>
          <p className="text-pv-text-secondary-text">
            Drill-down fields help the system understand how users can dissect
            and analyse a given data point.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <ExistingDDList
            selTables={selTables}
            setSelTables={setSelTables}
            logoList={logoList}
            updateDrillDownTableHandler={updateDrillDownTableHandler}
            primarySource={newMetricDetails?.primarySource || ''}
            applyBtnDisabled={isEqual(selTables, bkpSelTables)}
          />
          <NewDDList
            selTables={selTables}
            setSelTables={setSelTables}
            updateDrillDownTableHandler={updateDrillDownTableHandler}
            primarySource={newMetricDetails?.primarySource || ''}
            applyBtnDisabled={isEqual(selTables, bkpSelTables)}
          />
        </div>
      </div>
      <div className="flex flex-col px-3 py-2 gap-3 border-b border-pv-neutral-grey-100">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-pv-text-secondary-text">
            Primary source:
          </span>
          <span className="text-pv-text-secondary-text text-xs font-medium flex gap-1">
            <img
              src={logoList?.[newMetricDetails?.platform]}
              className="w-4"
              style={{
                aspectRatio: 1,
              }}
            />
            <span>{newMetricDetails?.primarySource}</span>
          </span>
        </div>
        <div className="flex items-center gap-2 bg-pv-primary-primary-50 px-2.5 py-2 rounded-lg">
          <Info
            size={18}
            weight="fill"
            className="text-pv-primary-primary-500"
          />
          <p className="text-xs">
            This source table is linked to other drill-down tables through
            different relationships. Suggested relationships are shown below
            based on connected fields in your dictionary.
          </p>
        </div>
      </div>
      <div
        className="w-full px-4 py-3 flex gap-3"
        style={{
          minHeight: 'calc(100% - 59px - 79px)',
          zIndex: 0,
        }}
      >
        <div className="h-full w-[30%] border border-pv-neutral-grey-100 flex items-center justify-center rounded-lg">
          <DrillDownTables
            bkpSelTables={bkpSelTables}
            selected={selected}
            setSelected={setSelected}
            loading={loading}
            logoList={logoList}
            tablePaths={tablePaths}
            setSelTables={setSelTables}
            setBkpSelTables={setBkpSelTables}
            setTablePaths={setTablePaths}
            pathsLoading={pathsLoading}
            pathDetails={pathDetails}
            primarySource={newMetricDetails?.primarySource || ''}
          />
        </div>
        <div className="h-full w-[70%] border border-pv-neutral-grey-100 flex items-center justify-center rounded-lg">
          <DrillDownPaths
            selected={selected}
            loading={loading}
            pathDetails={pathDetails}
            primarySource={newMetricDetails?.primarySource || ''}
            tablePaths={tablePaths}
            setTablePaths={setTablePaths}
            newMetricDetails={newMetricDetails}
            logoList={logoList}
            pathsLoading={pathsLoading}
          />
        </div>
      </div>
    </div>
  );
};
